import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, GitBranch, Loader2, Pencil } from "lucide-react";
import { nutritionAPI } from "@/lib/nutrition-api";
import { queryKeys } from "@/lib/query-keys";
import { useToast } from "@/components/ui/use-toast";
import {
  buildFoodLockedReason,
  convertUnit,
  FOOD_NUTRIENT_BASIS_LABELS,
  foodServingsEditable,
  formatNutrientAmount,
  getFoodTypeLabel,
  type FoodNutrientBasis,
} from "@/lib/nutrition-form-utils";
import { useNutrientCatalog } from "@/hooks/useNutrientCatalog";
import {
  DraftInProgressPill,
  FoodCodeBadge,
} from "@/components/settings/nutrition-shared";
import { FoodVersionHistory } from "@/components/settings/FoodVersionHistory";
import { FoodServingsCard } from "@/components/settings/FoodServingsCard";
import type { FoodVersionFields } from "@/lib/food-version-api";

// Local row descriptor for the basic-info table (name / hindi / full name).
// Nutrient fields use the same shape; the runtime catalog produces them.
interface NutrientField {
  name: string;
  label: string;
  unit: string;
}
import { useTagMetadata } from "@/hooks/useNutritionDropdowns";
import { TagPickerRow } from "@/components/settings/TagPickerRow";
import { cn } from "@/lib/utils";

interface NutrientValue {
  nutrient_name: string;
  unit: string;
  group: string;
  amount: string;
  nutrient_slug?: string;
}

interface TagObject {
  id: number;
  name: string;
  slug: string;
  [key: string]: any;
}

type TagField = TagObject[] | number[] | undefined;

// The versioning block (contract §2) is additive on the existing food read, so
// it is intersected in rather than duplicated. Every field is optional until
// the versioning branch deploys, which is what lets this screen render either
// way instead of erroring on a payload that predates it.
interface FoodItemDetail extends FoodVersionFields {
  id: number;
  food_code: string;
  name: string;
  hindi_name: string;
  full_name: string;
  food_type?: string;
  nutrient_basis?: FoodNutrientBasis;
  is_homemade?: boolean;
  is_favorite?: boolean;
  energy_kcal: string;
  water_g: string;
  source?: string;
  source_id?: string;
  source_web?: string;
  source_pdf?: string;
  brand?: string;
  nutrient_values: NutrientValue[];
  tag_info?: {
    food_item: number;
    texture?: string;
    cuisine_tags?: TagField;
    feeding_categories?: TagField;
    meal_types?: TagField;
    condition_tags?: TagField;
    allergen_tags?: TagField;
    age_suitability_tags?: TagField;
    context_tags?: TagField;
    validated_by?: string;
    validation_date?: string | null;
    source_confidence?: string;
    last_updated_by?: string;
    created_at?: string;
    updated_at?: string;
  };
}

const TAG_GROUPS: Array<{
  key: keyof NonNullable<FoodItemDetail["tag_info"]>;
  label: string;
}> = [
  { key: "cuisine_tags", label: "Cuisine Tags" },
  { key: "feeding_categories", label: "Feeding Method" },
  { key: "meal_types", label: "Meal Context" },
  { key: "condition_tags", label: "Condition Suitability" },
  { key: "allergen_tags", label: "Allergen Tags" },
  { key: "age_suitability_tags", label: "Age Suitability" },
  { key: "context_tags", label: "Context Tags" },
];

const isTagObjectArray = (value: unknown): value is TagObject[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  typeof value[0] === "object" &&
  value[0] !== null &&
  "name" in (value[0] as any);

// Read-only field row mirroring the Edit form's labelled rows.
const ReadField = ({
  label,
  labelWidth = "170px",
  value,
  unit,
  className,
}: {
  label: string;
  labelWidth?: string;
  value: React.ReactNode;
  unit?: string;
  className?: string;
}) => (
  <div className={cn("flex items-center gap-4", className)}>
    <span
      className="z-label shrink-0 mb-0 text-muted-foreground"
      style={{ width: labelWidth }}
    >
      {label}
    </span>
    <div className="relative flex-1 min-w-0">
      <div
        className={cn(
          "z-body flex h-9 w-full items-center rounded-md border border-input bg-muted/30 px-3 py-1 text-foreground",
          unit ? "pr-10" : ""
        )}
      >
        <span className="truncate">
          {value === "" || value === null || value === undefined ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            value
          )}
        </span>
      </div>
      {unit && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 z-hint text-muted-foreground pointer-events-none">
          {unit}
        </span>
      )}
    </div>
  </div>
);

// Source web / PDF addresses open in a new tab. Scheme-less addresses
// ("www.example.com") get https://; anything that isn't http(s) after that
// (e.g. javascript:) renders as plain text rather than a link.
const toSafeHref = (raw: string): string | null => {
  const trimmed = raw.trim();
  const withScheme = /^[a-z][a-z\d+.-]*:/i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.href
      : null;
  } catch {
    return null;
  }
};

const SourceLink = ({ url }: { url?: string | null }) => {
  if (!url?.trim()) return <span className="text-muted-foreground">—</span>;
  const href = toSafeHref(url);
  if (!href) return <>{url}</>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline-offset-4 hover:underline"
      title={url}
    >
      {url}
    </a>
  );
};

interface ReadOnlyNutrientTableField {
  name: string;
  label: string;
  unit: string;
  /** Canonical slug from the runtime catalog. Absent for basic-info rows. */
  slug?: string;
}

interface ReadOnlyNutrientTableProps {
  title: string;
  fields: ReadOnlyNutrientTableField[];
  valueBySlug: Record<string, { amount: string; unit: string; name: string }>;
  /** Catalog-derived slug → canonical display unit. */
  unitBySlug: Record<string, string>;
}

interface NutrientCell {
  value: string;
  unit: string;
}

// A catalog nutrient, converted to the catalog's standard unit.
const catalogCell = (
  field: ReadOnlyNutrientTableField,
  valueBySlug: ReadOnlyNutrientTableProps["valueBySlug"],
  unitBySlug: Record<string, string>
): NutrientCell => {
  const entry = field.slug ? valueBySlug[field.slug] : undefined;
  if (!entry) return { value: "—", unit: field.unit };

  const standardUnit =
    (field.slug && unitBySlug[field.slug]) || entry.unit || field.unit;
  const converted = convertUnit(
    parseFloat(entry.amount),
    entry.unit || standardUnit,
    standardUnit
  );
  return {
    value: formatNutrientAmount(converted, field.name),
    unit: standardUnit,
  };
};

const ReadOnlyNutrientTable = ({
  title,
  fields,
  valueBySlug,
  unitBySlug,
}: ReadOnlyNutrientTableProps) => {
  if (fields.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="bg-muted/50 border-b border-border px-3 py-2">
        <h4 className="z-label text-foreground">{title}</h4>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-border/60 hover:bg-transparent">
            <TableHead className="z-eyebrow text-muted-foreground h-8 px-3">
              Nutrient
            </TableHead>
            <TableHead className="z-eyebrow text-muted-foreground h-8 px-3 text-right">
              Amount
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field) => {
            const { value: displayValue, unit: displayUnit } = catalogCell(
              field,
              valueBySlug,
              unitBySlug
            );

            return (
              <TableRow
                key={field.name}
                className="border-border/60 hover:bg-muted/20"
              >
                <TableCell
                  className="px-3 py-2 z-caption text-foreground"
                  title={field.label}
                >
                  {field.label}
                </TableCell>
                <TableCell className="px-3 py-2">
                  <div className="ml-auto flex w-32 items-center justify-end gap-2 z-caption tabular-nums">
                    <span>{displayValue}</span>
                    {displayUnit && (
                      <span className="z-hint text-muted-foreground">
                        {displayUnit}
                      </span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export const ViewFood = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  // The food list passes its search/filters along, so "Back to Food Items"
  // returns to the same results rather than the full list.
  const foodListSearch =
    (location.state as { foodListSearch?: string } | null)?.foodListSearch ??
    "";
  const { patientId } = useParams<{ patientId: string }>();
  const {
    catalog,
    isLoading: isLoadingCatalog,
    isError: isCatalogError,
    refetch: refetchCatalog,
  } = useNutrientCatalog();

  const foodItemId = patientId ? parseInt(patientId, 10) : NaN;
  const isValidId = !isNaN(foodItemId);

  const {
    data: foodItem,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.nutrition.foodItems.detail(foodItemId),
    queryFn: () =>
      nutritionAPI.foodItems.get(foodItemId) as Promise<FoodItemDetail>,
    enabled: isValidId,
    retry: false,
  });

  const { data: tagMetadata } = useTagMetadata();

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description:
          (error as any)?.message || "Failed to fetch food item details",
        variant: "destructive",
      });
    }
  }, [error, toast]);

  // "Edit food" is gone: a published food is never mutated in place. The
  // affordance is "Create new version", which opens the same editor as a draft
  // workspace — the live version keeps serving pickers and pinned entries until
  // the draft is published. When a draft is already staged, this continues it
  // (drafts are shared per food, one workspace, never forked).
  const [newVersionDialogOpen, setNewVersionDialogOpen] = useState(false);

  const hasStagedDraft = foodItem?.has_unpublished_changes ?? false;
  const draftTarget = foodItem?.draft_target_version_number ?? null;
  const draftEditor = foodItem?.draft_updated_by ?? null;
  // Server-owned: false on a retired food. Default true so an older payload
  // without capabilities doesn't wrongly lock editing.
  const canEdit = foodItem?.capabilities?.can_edit ?? true;
  // Servings have their own flag: true on a reference-source food even though
  // `can_edit` is false there.
  const canEditServings = foodServingsEditable(foodItem?.capabilities);

  const goToDraftEditor = () => {
    if (foodItem) {
      navigate(
        `/settings/nutrition/update-food-entry?foodItemId=${foodItem.id}`
      );
    }
  };

  // Build slug → value lookup mirroring Edit form's reverse mapping.
  // For responses where nutrient_slug is absent (pre-ZCS-345 records or edge
  // cases), fall back to matching by lowercased display name via the catalog's
  // labelBySlug reverse map so the value still resolves to the canonical slug.
  const valueBySlug = useMemo<
    Record<string, { amount: string; unit: string; name: string }>
  >(() => {
    if (!foodItem) return {};
    const map: Record<string, { amount: string; unit: string; name: string }> =
      {};

    const slugByLowerLabel: Record<string, string> = {};
    if (catalog) {
      Object.entries(catalog.labelBySlug).forEach(([slug, label]) => {
        slugByLowerLabel[label.toLowerCase()] = slug;
      });
    }

    foodItem.nutrient_values.forEach((n) => {
      const resolvedSlug =
        n.nutrient_slug ||
        slugByLowerLabel[n.nutrient_name?.toLowerCase().trim() ?? ""];
      const key = resolvedSlug || n.nutrient_name?.toLowerCase();
      if (key) {
        map[key] = {
          amount: n.amount,
          unit: n.unit,
          name: n.nutrient_name?.replace(/_/g, " ").trim() || key,
        };
      }
    });
    return map;
  }, [foodItem, catalog]);

  // Resolve source-confidence label from metadata (API stores raw value).
  const sourceConfidenceLabel = useMemo(() => {
    if (!foodItem?.tag_info?.source_confidence || !tagMetadata) {
      return foodItem?.tag_info?.source_confidence || "";
    }
    const opt = tagMetadata.choices?.source_confidence?.find(
      (o: any) => o.value === foodItem.tag_info?.source_confidence
    );
    return opt?.label || foodItem.tag_info.source_confidence;
  }, [foodItem, tagMetadata]);

  const textureLabel = useMemo(() => {
    if (!foodItem?.tag_info?.texture || !tagMetadata) {
      return foodItem?.tag_info?.texture || "";
    }
    const opt = tagMetadata.choices?.texture?.find(
      (o: any) => o.value === foodItem.tag_info?.texture
    );
    return opt?.label || foodItem.tag_info.texture;
  }, [foodItem, tagMetadata]);

  const nameFields: NutrientField[] = [
    { name: "name", label: "Food Name", unit: "" },
    { name: "hindiName", label: "Hindi Name", unit: "" },
    { name: "fullName", label: "Full Name", unit: "" },
  ];
  const nameValues: Record<string, string> = foodItem
    ? {
        name: foodItem.name || "",
        hindiName: foodItem.hindi_name || "",
        fullName: foodItem.full_name || "",
      }
    : {};

  // The General table is exactly what the backend's catalog lists; Energy and
  // Water arrive as one nutrient row each, so the console adds none of its own.
  const generalTableFields: ReadOnlyNutrientTableField[] =
    catalog?.categories.find((c) => c.title === "General")?.fields ?? [];

  const otherCategories =
    catalog?.categories.filter((c) => c.title !== "General") ?? [];
  const unitBySlug = catalog?.unitBySlug ?? {};

  if (isCatalogError) {
    return (
      <div className="h-full flex flex-col min-h-0 gap-4">
        <Card className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <span className="z-body">Failed to load nutrient catalog.</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetchCatalog()}
            >
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoadingCatalog || !catalog) {
    return (
      <div className="h-full flex flex-col min-h-0 gap-4">
        <Card className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            <span className="z-body">Loading nutrient catalog…</span>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0 gap-4">
      <Card className="max-w-[2000px] flex-1 flex flex-col min-h-0 overflow-visible">
        <CardHeader className="shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/settings/nutrition/view-food-items${foodListSearch}`
                  )
                }
                className="z-caption text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Food Items
              </button>
              <p className="z-eyebrow text-muted-foreground">Viewing</p>
              <CardTitle className="z-h2 truncate">
                {foodItem?.name || (isLoading ? "Loading…" : "Food not found")}
              </CardTitle>
              {foodItem && (
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {/* The code moved out of the meta line below and into the
                      badge, which carries the published version with it. A food
                      always exists as published — unlike a recipe, it never
                      starts life as a draft. */}
                  <FoodCodeBadge
                    code={foodItem.food_code || null}
                    isPublished
                    versionNumber={foodItem.food_version_number ?? null}
                  />
                  {/* Not selectable for new entries, but still correct on every
                      entry already pinned to it. Neutral, not alarming: an old
                      plan built on a retired food is expected, not a problem. */}
                  {foodItem.is_retired && (
                    <Badge variant="neutral">Retired</Badge>
                  )}
                  {/* A staged draft routes into itself — there is one shared
                      draft per food, so continuing it never forks a second. */}
                  {hasStagedDraft && (
                    <DraftInProgressPill
                      compact
                      targetVersionNumber={draftTarget}
                      editorName={draftEditor}
                      onClick={() => setNewVersionDialogOpen(true)}
                    />
                  )}
                </div>
              )}
              {foodItem && (
                <p className="z-caption text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                  {foodItem.energy_kcal && (
                    <span>
                      {formatNutrientAmount(foodItem.energy_kcal, "energyKcal")}{" "}
                      kcal
                    </span>
                  )}
                  {foodItem.full_name &&
                    foodItem.full_name !== foodItem.name && (
                      <>
                        {foodItem.energy_kcal && <span aria-hidden>·</span>}
                        <span className="truncate">{foodItem.full_name}</span>
                      </>
                    )}
                </p>
              )}
            </div>
            {foodItem && (
              <>
                <Button
                  onClick={() => setNewVersionDialogOpen(true)}
                  disabled={!canEdit}
                  title={
                    !canEdit
                      ? buildFoodLockedReason(foodItem, foodItem.name)
                      : undefined
                  }
                >
                  {hasStagedDraft ? (
                    <>
                      <Pencil className="h-4 w-4 mr-2" />
                      Continue draft
                    </>
                  ) : (
                    <>
                      <GitBranch className="h-4 w-4 mr-2" />
                      Create new version
                    </>
                  )}
                </Button>
                <AlertDialog
                  open={newVersionDialogOpen}
                  onOpenChange={setNewVersionDialogOpen}
                >
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {hasStagedDraft
                          ? "Continue the draft in progress?"
                          : "Create a new version?"}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {hasStagedDraft ? (
                          <>
                            This food already has an unpublished draft
                            {draftEditor ? ` by ${draftEditor}` : ""}. Editing
                            continues that shared draft — it won't start a
                            second one. The live version keeps serving pickers
                            and pinned plans until the draft is published.
                          </>
                        ) : (
                          <>
                            Editing never changes the live food. Your changes
                            are staged as a draft; the current version
                            {foodItem.food_version_number != null
                              ? ` (v${foodItem.food_version_number})`
                              : ""}{" "}
                            keeps serving pickers and pinned plans until you
                            publish.
                          </>
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={goToDraftEditor}>
                        {hasStagedDraft ? "Continue draft" : "Start editing"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto pt-2 pb-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground/70" />
            </div>
          ) : !foodItem ? null : (
            <Tabs defaultValue="info">
              <TabsList>
                <TabsTrigger value="info">Info</TabsTrigger>
                <TabsTrigger value="classification">
                  Classification &amp; Diet
                </TabsTrigger>
                <TabsTrigger value="nutrients">Nutrient Profile</TabsTrigger>
                <TabsTrigger value="servings">Servings</TabsTrigger>
                <TabsTrigger value="versions">Version History</TabsTrigger>
              </TabsList>

              {/* Info tab — mirrors Edit Food layout */}
              <TabsContent value="info" className="mt-4">
                <Card>
                  <CardContent className="pt-6 pb-5 space-y-4">
                    {/* Name fields - 2 per row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                      {nameFields.map((field) => (
                        <ReadField
                          key={field.name}
                          label={field.label}
                          labelWidth="120px"
                          value={nameValues[field.name]}
                        />
                      ))}

                      {/* Food Type - locked, full row */}
                      <div className="lg:col-span-2">
                        <div className="flex items-center gap-4">
                          <span className="z-label w-[120px] shrink-0 text-muted-foreground">
                            Food Type
                          </span>
                          <div className="z-body flex h-9 max-w-[300px] flex-1 items-center rounded-md border border-input bg-muted px-3 text-foreground">
                            <span className="truncate">
                              {foodItem.food_type ? (
                                getFoodTypeLabel(foodItem.food_type)
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </span>
                          </div>
                          <span className="z-hint text-muted-foreground">
                            (Locked - cannot be changed)
                          </span>
                        </div>
                      </div>

                      {/* Nutrient Basis - what the values below describe */}
                      <div className="lg:col-span-2">
                        <div className="flex items-center gap-4">
                          <span className="z-label w-[120px] shrink-0 text-muted-foreground">
                            Nutrient Basis
                          </span>
                          <div className="z-body flex h-9 max-w-[300px] flex-1 items-center rounded-md border border-input bg-muted px-3 text-foreground">
                            <span className="truncate">
                              {
                                FOOD_NUTRIENT_BASIS_LABELS[
                                  foodItem.nutrient_basis ?? "100G"
                                ]
                              }
                            </span>
                          </div>
                          <span className="z-hint text-muted-foreground">
                            (Locked - set when the food was created)
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Source */}
                    <ReadField label="Source" value={foodItem.source || ""} />

                    {/* Source Web / PDF */}
                    <ReadField
                      label="Source Web"
                      value={<SourceLink url={foodItem.source_web} />}
                    />
                    <ReadField
                      label="Source PDF"
                      value={<SourceLink url={foodItem.source_pdf} />}
                    />

                    {/* Source Confidence */}
                    <ReadField
                      label="Source Confidence"
                      value={sourceConfidenceLabel}
                    />

                    {/* Other fields - 3-col grid (Brand) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                      <ReadField
                        label="Brand"
                        labelWidth="70px"
                        value={foodItem.brand || ""}
                      />
                    </div>

                    {/* Boolean fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3">
                      <div className="flex items-center gap-2">
                        <span className="z-label text-muted-foreground">
                          Favorite
                        </span>
                        <Badge
                          variant={
                            foodItem.is_favorite ? "default" : "secondary"
                          }
                        >
                          {foodItem.is_favorite ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="z-label text-muted-foreground">
                          Homemade
                        </span>
                        <Badge
                          variant={
                            foodItem.is_homemade ? "default" : "secondary"
                          }
                        >
                          {foodItem.is_homemade ? "Yes" : "No"}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Classification & Diet tab — read-only chip rows mirroring Edit Food */}
              <TabsContent value="classification" className="mt-4">
                <Card>
                  <CardContent className="space-y-6 pt-6 pb-5">
                    <div className="rounded-lg border border-border/60 overflow-hidden">
                      {TAG_GROUPS.map(({ key, label }) => {
                        const tags = foodItem.tag_info?.[key] as TagField;
                        const tagObjects = isTagObjectArray(tags)
                          ? (tags as TagObject[])
                          : [];
                        return (
                          <div key={key} className="px-3">
                            <TagPickerRow
                              label={label}
                              options={tagObjects}
                              selectedIds={tagObjects.map((t) => t.id)}
                              emptyText="—"
                            />
                          </div>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                      <ReadField label="Texture" value={textureLabel} />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Nutrient Profile tab — mirrors Edit Food's tables */}
              <TabsContent value="nutrients" className="mt-4">
                <Card>
                  <CardContent className="pt-6 pb-5 space-y-3">
                    <h3 className="z-h3">Nutrient Profile</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <ReadOnlyNutrientTable
                        title="General"
                        fields={generalTableFields}
                        valueBySlug={valueBySlug}
                        unitBySlug={unitBySlug}
                      />
                      {otherCategories.map((cat) => (
                        <ReadOnlyNutrientTable
                          key={cat.title}
                          title={cat.title}
                          fields={cat.fields}
                          valueBySlug={valueBySlug}
                          unitBySlug={unitBySlug}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Servings tab */}
              <TabsContent value="servings" className="mt-4">
                {/* The same card as Settings → Food Servings. Gated on
                    can_edit_servings, not can_edit: a reference-source food
                    (IFCT, USDA, USFDA) locks its profile but still takes
                    servings, which are Zarity's household measures. */}
                <FoodServingsCard
                  foodItemId={foodItem.id}
                  foodItemName={foodItem.name}
                  nutrientBasis={foodItem.nutrient_basis ?? "100G"}
                  canEdit={canEditServings}
                  lockedReason={
                    canEditServings
                      ? undefined
                      : buildFoodLockedReason(
                          foodItem,
                          foodItem.name,
                          "servings"
                        )
                  }
                />
              </TabsContent>

              {/* Version history — read-only (ZCS-463, contract §4). */}
              <TabsContent value="versions" className="mt-4">
                <FoodVersionHistory foodItemId={foodItemId} />
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ViewFood;
